import { useState } from 'react';
import api from '../../../api';
import { OrderStatusNotifySheet } from '../OrderStatusNotifySheet';
import { STATUS_NOTE_TEMPLATE, STATUSES, nextStatuses, statusMeta } from '../../../pages/admin/adminOrdersShared';
import { OPS_STATUS_HINTS } from '../../../lib/onvizOrderJourney';
import { ORDER_STATUS_LABELS } from './crmLabels';

type Props = {
  orderId: number;
  orderNumber: string;
  currentStatus: string;
  hasTelegram: boolean;
  disabled?: boolean;
  compact?: boolean;
  onUpdated: () => void;
  hapticSelection: () => void;
  hapticSuccess?: () => void;
};

export function CrmOrderStatusPanel({
  orderId,
  orderNumber,
  currentStatus,
  hasTelegram,
  disabled,
  compact,
  onUpdated,
  hapticSelection,
  hapticSuccess,
}: Props) {
  const [pending, setPending] = useState<{ status: string; initialNote: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const requestChange = (newStatus: string) => {
    if (newStatus === currentStatus || disabled) return;
    hapticSelection();
    const initialNote = STATUS_NOTE_TEMPLATE[newStatus] || '';
    setPending({ status: newStatus, initialNote });
  };

  const confirmChange = async (opts: { notifyClient: boolean; managerNote: string }) => {
    if (!pending) return;
    setBusy(true);
    setPending(null);
    try {
      await api.patch(`/admin/orders/${orderId}`, {
        status: pending.status,
        manager_notes: opts.managerNote || undefined,
        notify_client: opts.notifyClient,
      });
      hapticSuccess?.();
      onUpdated();
    } finally {
      setBusy(false);
    }
  };

  const quick = nextStatuses(currentStatus);

  return (
    <div className="space-y-2">
      <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-2)' }}>
        <b style={{ color: 'var(--cyan)' }}>Статус для клиента</b> — как раньше во вкладке «Заказы»: при
        сохранении можно отправить сообщение в Telegram. Воронка выше синхронизируется автоматически.
      </p>
      <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
        Сейчас:{' '}
        <span className="font-bold" style={{ color: 'var(--text-1)' }}>
          {ORDER_STATUS_LABELS[currentStatus] || currentStatus}
        </span>
        {OPS_STATUS_HINTS[currentStatus] ? ` — ${OPS_STATUS_HINTS[currentStatus]}` : ''}
      </p>
      {!compact && quick.length > 0 && currentStatus !== 'delivered' && currentStatus !== 'cancelled' && (
        <div className="flex flex-wrap gap-1.5">
          {quick.map((ns) => {
            const m = statusMeta(ns);
            return (
              <button
                key={ns}
                type="button"
                disabled={busy || disabled}
                onClick={() => requestChange(ns)}
                className="press px-2.5 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
                style={{ background: m.glassColor, color: m.textColor, border: `1px solid ${m.borderColor}` }}
              >
                → {m.label}
              </button>
            );
          })}
        </div>
      )}
      <div className={`grid gap-1.5 ${compact ? 'grid-cols-2' : 'grid-cols-2'}`}>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            disabled={currentStatus === s.value || busy || disabled}
            onClick={() => requestChange(s.value)}
            className="press flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[11px] font-semibold disabled:opacity-40 touch-manipulation min-h-[44px]"
            style={
              currentStatus === s.value
                ? { background: s.glassColor, color: s.textColor, border: `1px solid ${s.borderColor}` }
                : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
            }
          >
            <s.icon size={12} />
            {s.label}
          </button>
        ))}
      </div>

      {pending && (
        <OrderStatusNotifySheet
          orderNumber={orderNumber}
          newStatus={pending.status}
          hasTelegram={hasTelegram}
          initialNote={pending.initialNote}
          onCancel={() => setPending(null)}
          onConfirm={(opts) => void confirmChange(opts)}
        />
      )}
    </div>
  );
}
